import { createContext, type ReactNode, useContext, useMemo, useState } from 'react';
import { useInvoiceParamsPresence, usePhieuXuatKhoExport } from '../hooks';
import { MA_CHUNG_TU, QUERY_KEYS } from '@/constants';
import { useCRUD } from '@/hooks';

interface PhieuXuatKhoSearchContextValue {
  exportToExcel: () => Promise<void>;
  canExport: boolean;
  isExporting: boolean;
  hasInvoiceParams: boolean;
  isImportDialogOpen: boolean;
  openImportDialog: () => void;
  closeImportDialog: () => void;
  transactionOptions: { value: string; label: string }[];
}

const PhieuXuatKhoSearchContext = createContext<PhieuXuatKhoSearchContextValue | null>(null);

export function usePhieuXuatKhoSearchContext() {
  const context = useContext(PhieuXuatKhoSearchContext);

  if (!context) {
    throw new Error('usePhieuXuatKhoSearchContext must be used within PhieuXuatKhoSearchProvider');
  }

  return context;
}

export function PhieuXuatKhoSearchProvider({ children }: { children: ReactNode }) {
  const { exportToExcel, canExport, isExporting } = usePhieuXuatKhoExport();
  const hasInvoiceParams = useInvoiceParamsPresence();
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const { data } = useCRUD({
    endpoint: `${QUERY_KEYS.MA_GIAO_DICH}?ma_ct=${MA_CHUNG_TU.KHO.PHIEU_XUAT_KHO}`
  });

  const transactionOptions = useMemo(
    () =>
      data.map(item => ({
        value: item.uuid,
        label: item.ten_gd
      })),
    [data]
  );

  const value = useMemo(
    () => ({
      exportToExcel,
      canExport,
      isExporting,
      hasInvoiceParams,
      isImportDialogOpen,
      openImportDialog: () => setIsImportDialogOpen(true),
      closeImportDialog: () => setIsImportDialogOpen(false),
      transactionOptions
    }),
    [canExport, exportToExcel, hasInvoiceParams, isExporting, isImportDialogOpen, transactionOptions]
  );

  return <PhieuXuatKhoSearchContext.Provider value={value}>{children}</PhieuXuatKhoSearchContext.Provider>;
}
